import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash2, Edit3, Upload, Printer, Tag, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Popcorn", "Nachos", "Getränke", "Süßigkeiten", "Menüs", "Extras"];

async function _printPriceSigns(body, printOffice = false) {
  if (printOffice) {
    await api.post("/admin/signage/price-signs?print_office=true", body);
    return null;
  }
  const { data } = await api.post("/admin/signage/price-signs", body, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return url;
}

function PriceTagButtons({ product, category = null, className = "" }) {
  const [busy, setBusy] = useState(false);
  const build = () => {
    if (product) {
      return { layout: "individual", title: (product.category || "SNACKBAR").toUpperCase(),
        items: [{ name: product.name, price: Number(product.sell_price) || 0, sub: product.category || "" }] };
    }
    return { layout: "individual", title: (category || "SNACKBAR").toUpperCase(),
      include_all_products: true, ...(category ? { category } : {}) };
  };
  const preview = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await _printPriceSigns(build(), false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const printOff = async (e) => {
    e.stopPropagation();
    const label = product ? `Preisschild für „${product.name}"` : `alle Preisschilder${category ? ` (${category})` : ""}`;
    if (!window.confirm(`${label} an den Bürodrucker senden?`)) return;
    setBusy(true);
    try { await _printPriceSigns(build(), true); toast.success("An Drucker gesendet"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bürodrucker nicht erreichbar"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`inline-flex rounded border border-[#2A2A2A] overflow-hidden ${className}`}>
      <button onClick={preview} disabled={busy}
        data-testid={product ? `product-price-preview-${product.id}` : `price-preview-all${category ? `-${category}` : ""}`}
        title="Preisschild-Vorschau (PDF)"
        className="text-xs px-2 py-1 text-[#EEDC82] hover:bg-[#EEDC82]/10 disabled:opacity-40 flex items-center gap-1">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
      </button>
      <button onClick={printOff} disabled={busy}
        data-testid={product ? `product-price-print-${product.id}` : `price-print-all${category ? `-${category}` : ""}`}
        title="Preisschild an Bürodrucker senden"
        className="text-xs px-2 py-1 text-[#D4AF37] border-l border-[#2A2A2A] hover:bg-[#D4AF37]/10 disabled:opacity-40 flex items-center gap-1">
        <Printer size={12} />
      </button>
    </div>
  );
}
const EMPTY = { name: "", category: "Extras", image_url: "", description: "", sell_price: 3, buy_price: 1, stock: 100, min_stock: 10, unit: "Stück", variants: [], add_ons: [], allergens: [], available: true, sort_order: 100, barcode: "" };

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Produkt löschen?")) return;
    await api.delete(`/products/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Sortiment</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Produkte</h1>
        </div>
        <div className="flex items-center gap-2">
          <PriceTagButtons className="!border-[#EEDC82]/50" />
          <button data-testid="product-add-btn" onClick={() => { setEdit({ ...EMPTY }); setOpen(true); }}
            className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] transitions-safe">
            <Plus size={16} /> Neues Produkt
          </button>
        </div>
      </div>
      {CATS.map((c) => {
        const list = items.filter((i) => i.category === c);
        if (!list.length) return null;
        return (
          <div key={c}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <h3 className="font-heading text-xl flex items-center gap-2"><Tag size={16} className="text-[#EEDC82]" /> {c}</h3>
              <PriceTagButtons category={c} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {list.map((p) => (
                <div key={p.id} className="card-lux p-3">
                  <div className="flex justify-between text-sm">
                    <div className="font-medium">{p.name}</div>
                    <div className={`text-[10px] uppercase tracking-widest ${p.available ? "text-[#2E7D32]" : "text-[#EE9CA1]"}`}>{p.available ? "aktiv" : "aus"}</div>
                  </div>
                  <div className="text-xs text-[#A8A398]">{p.sell_price?.toFixed(2)} €</div>
                  <div className="text-xs text-[#A8A398]">Best.: {p.stock} / min {p.min_stock}</div>
                  {p.barcode && <div className="text-[10px] text-[#575148] font-mono mt-0.5" title="Barcode / EAN">⁞|| {p.barcode}</div>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setEdit(p); setOpen(true); }} className="flex-1 text-xs py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37]"><Edit3 size={12} /></button>
                    <PriceTagButtons product={p} />
                    <button onClick={() => remove(p.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {open && <ProductDialog initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ProductDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImg = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    upd("image_url")(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    toast.success("Bild hochgeladen");
  };
  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.patch(`/products/${form.id}`, form);
      else await api.post("/products", form);
      toast.success("Gespeichert"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">Produkt</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <F label="Name" v={form.name} on={upd("name")} testid="product-name" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kategorie</label>
            <select value={form.category} onChange={(e) => upd("category")(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <F label="Verkaufspreis (€)" v={form.sell_price} on={(v) => upd("sell_price")(parseFloat(v||0))} type="number" />
          <F label="Einkaufspreis (€)" v={form.buy_price} on={(v) => upd("buy_price")(parseFloat(v||0))} type="number" />
          <F label="Lagerbestand" v={form.stock} on={(v) => upd("stock")(parseInt(v||0))} type="number" />
          <F label="Mindestbestand" v={form.min_stock} on={(v) => upd("min_stock")(parseInt(v||0))} type="number" />
          <F label="Einheit" v={form.unit} on={upd("unit")} />
          <F label="Sortierung" v={form.sort_order} on={(v) => upd("sort_order")(parseInt(v||0))} type="number" />
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Barcode / EAN (Scanner)</label>
            <input data-testid="product-barcode" value={form.barcode || ""}
              onChange={(e) => upd("barcode")(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              placeholder="einfach Scanner nutzen · z.B. 4008400300818"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none" />
            <div className="text-[10px] text-[#575148] mt-1">Wird beim Scannen an Kasse und Snackbar automatisch als Produkt erkannt.</div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Beschreibung</label>
            <textarea rows={2} value={form.description || ""} onChange={(e) => upd("description")(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bild</label>
            <input value={form.image_url || ""} onChange={(e) => upd("image_url")(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            <label className="mt-2 flex items-center gap-2 py-2 px-3 border border-dashed border-[#2A2A2A] rounded cursor-pointer text-xs text-[#A8A398] w-fit"><Upload size={12} /> Bild hochladen
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])} />
            </label>
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-24 rounded" />}
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" checked={form.available} onChange={(e) => upd("available")(e.target.checked)} />
            Verfügbar
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="product-save-btn" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">
            {busy ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
    </div>
  );
}
